import { useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  casePageUrl,
  correspondenceFileUrl,
  useCase,
  type AttachmentOut,
  type CaseRecordOut,
  type CorrespondenceOut,
  type DocumentOut,
  type TimelineItemOut,
} from "@/api";
import { Button } from "@/components/Button";
import { SketchViewer } from "@/components/SketchViewer";
import { DownloadIcon, FileIcon } from "@/components/icons";
import segmented from "@/components/Segmented.module.css";
import { Tabs } from "@/components/Tabs";
import { dayHeading, dayKey, formatBytes, formatDay } from "@/lib/format";
import { outlineFor } from "@/lib/noStand";
import { HANDOVER_CLOSED } from "@/lib/stages";
import { useGlide } from "@/lib/useGlide";

import { adoptIntoDrawings } from "./adopt";
import { AdoptDialog } from "./AdoptDialog";
import { bundleEvents, type TimelineRow } from "./bundle";
import { customerAssignedSentence, CustomerFromMailDialog } from "./CustomerFromMailDialog";
import { DropTarget, FilingRows, IntakeStatus, IntakeTools, useFileIntake, type FileIntake } from "./FileDrop";
import { EntryMarksContext } from "./entryMarks";
import { Feed } from "./Feed";
import { NoteForm } from "./NoteForm";
import { RetractDialog } from "./RetractDialog";
import { EventBundle } from "./EventBundle";
import { DownloadLink, TimelineEntry } from "./TimelineEntry";
import styles from "./Record.module.css";

/** How long a just-filed entry stays marked (UX gate round 2, #5). */
export const FRESH_MS = 2000;

export type TimelineFilter = "all" | "mail" | "note" | "file" | "system";

const FILTERS: readonly { value: TimelineFilter; label: string }[] = [
  { value: "all", label: "Alles" },
  { value: "mail", label: "Mails" },
  { value: "note", label: "Notizen" },
  { value: "file", label: "Dateien" },
  { value: "system", label: "System" },
];

/**
 * Filtered in the browser, not on the server (plan D-3): „System" is not a
 * correspondence kind the record route could filter by, and the tab count
 * „Verlauf · n" and the segment counts need the whole list anyway.
 */
export function filterTimeline(items: readonly TimelineItemOut[], filter: TimelineFilter): TimelineItemOut[] {
  if (filter === "all") return [...items];
  return items.filter((item) => {
    if (item.kind === "event") return filter === "system";
    const kind = item.correspondence.kind;
    if (filter === "mail") return kind === "email_in" || kind === "email_out";
    if (filter === "note") return kind === "note";
    return filter === "file" && kind === "file";
  });
}

/**
 * Entries by local calendar day (UX ruling 6, F-6), newest day first. The
 * server already sends newest first, so a day is a run of neighbours and
 * the order inside it is the server's.
 */
export function groupByDay(
  items: readonly TimelineItemOut[],
  now: Date = new Date(),
): { key: string; heading: string; items: TimelineItemOut[] }[] {
  const days: { key: string; heading: string; items: TimelineItemOut[] }[] = [];
  for (const item of items) {
    const key = dayKey(item.at);
    const last = days[days.length - 1];
    if (last !== undefined && last.key === key) last.items.push(item);
    else days.push({ key, heading: dayHeading(item.at, now), items: [item] });
  }
  return days;
}

function keyOf(item: TimelineItemOut): string {
  return item.kind === "event" ? `event-${item.event.id}` : item.correspondence.id;
}

function rowKey(row: TimelineRow): string {
  return row.kind === "bundle" ? row.key : keyOf(row.item);
}

/** A day's rows: runs of work events bundled where the list asks for it (design 2026-10-24 §6.3). */
function rowsOf(items: readonly TimelineItemOut[], bundle: boolean): TimelineRow[] {
  return bundle ? bundleEvents(items) : items.map((item) => ({ kind: "item", item }));
}

/**
 * How many rows rise in on the record's first draw, and how far apart
 * (design 2026-10-24 §6.4, §7: „≤ 240 ms gesamt"). Six rows 20 ms apart over
 * `--dur-2` (140 ms) end at 5 × 20 + 140 = 240 ms; eight 30 ms apart over
 * `--dur-3` took 430 (Task 6 ruling: the spec's total beats the plan's
 * numbers). Record.module.css writes the step into `.rise`'s delay —
 * Timeline.test.tsx holds the two together and the sum under 240.
 */
export const RISING_ROWS = 6;
export const RISE_STEP_MS = 20;

/**
 * When the first draw's rise is over and the mark comes off: 240 ms, and
 * some room. A row drawn again after that (a tab and back, a filter and
 * back) stands at once.
 */
const RISE_MS = 400;

/** The rows that rise on the record's first draw, by key, with their place in the stagger. */
function risingRows(items: readonly TimelineItemOut[]): ReadonlyMap<string, number> {
  const keys = groupByDay(items)
    .flatMap((day) => rowsOf(day.items, true))
    .slice(0, RISING_ROWS)
    .map(rowKey);
  return new Map(keys.map((key, index) => [key, index]));
}

/**
 * Days with a sticky head each, the mesh marker before it (a section title,
 * design 2026-10-24 §8.6); Task 9 hands the entries their gestures. The
 * record bundles runs of work events (`bundle`); the Stände list them all.
 */
function EntryList({
  caseId,
  items,
  bundle = false,
  rising = null,
}: {
  caseId: string;
  items: readonly TimelineItemOut[];
  bundle?: boolean;
  /** The first draw's rows and their place in the stagger (`risingRows`); `null` once it is over. */
  rising?: ReadonlyMap<string, number> | null;
}) {
  return (
    <ol className={styles.days}>
      {groupByDay(items).map((day) => (
        <li key={day.key} className={styles.dayGroup}>
          <h3 className={`label ${styles.day}`}>
            <span className="marke" aria-hidden="true" />
            {day.heading}
          </h3>
          <ol className={styles.entries}>
            {rowsOf(day.items, bundle).map((row) => {
              const key = rowKey(row);
              const place = rising?.get(key);
              return (
                <li
                  key={key}
                  className={place !== undefined ? styles.rise : undefined}
                  style={place !== undefined ? ({ "--i": place } as CSSProperties) : undefined}
                >
                  {row.kind === "bundle" ? (
                    <EventBundle items={row.items} />
                  ) : (
                    <TimelineEntry caseId={caseId} item={row.item} />
                  )}
                </li>
              );
            })}
          </ol>
        </li>
      ))}
    </ol>
  );
}

/**
 * Keeps a segment — ring included — inside the filter row. Only the row
 * scrolls: `scrollIntoView` would move the page sideways as well.
 */
function reveal(scroller: HTMLElement | null, segment: EventTarget | null) {
  if (scroller === null || !(segment instanceof HTMLElement)) return;
  // 2 px ring + 2 px offset, and one pixel for a segment's fractional width.
  const ring = 5;
  const start = segment.offsetLeft - ring;
  const end = segment.offsetLeft + segment.offsetWidth + ring;
  if (start < scroller.scrollLeft) scroller.scrollLeft = Math.max(0, start);
  else if (end > scroller.scrollLeft + scroller.clientWidth) scroller.scrollLeft = end - scroller.clientWidth;
}

/** The pressed segment into view. */
function revealActive(scroller: HTMLElement | null) {
  reveal(scroller, scroller?.querySelector('[aria-pressed="true"]') ?? null);
}

/** Which ends of the filter row hide a segment — a pixel of rounding is not „more". */
function hiddenEnds(scroller: HTMLElement): { start: boolean; end: boolean } {
  return {
    start: scroller.scrollLeft > 1,
    end: scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
  };
}

/**
 * How much of the row's end the fade covers (UX-Gutachten 1, B-17): from
 * the start of the segment the edge cuts to the edge, so that segment is
 * faded out whole and the last word read is a whole one — at 375 px the row
 * ended in „Datei". `0` where the edge cuts nothing; the CSS keeps its 2 rem.
 */
function endCover(scroller: HTMLElement): number {
  const edge = scroller.getBoundingClientRect().right;
  for (const segment of scroller.querySelectorAll("button")) {
    const box = segment.getBoundingClientRect();
    if (box.left < edge - 1 && box.right > edge + 1) return Math.round(edge - box.left);
  }
  return 0;
}

/** The ends to mark and the cover of the end, measured together. */
function measureRow(scroller: HTMLElement): { start: boolean; end: boolean; cover: number } {
  const ends = hiddenEnds(scroller);
  return { ...ends, cover: ends.end ? endCover(scroller) : 0 };
}

/**
 * The filter segments with their counts (UX ruling 6: „Mails 4"). One row
 * that scrolls sideways where the column is narrower than the row (record
 * fix wave: at 390 px „System 3" wrapped onto a line of its own); the
 * scroller keeps room around the row so a focus ring is never clipped.
 *
 * An end that hides a segment says so (UX gate round 3, #7): a fade and a
 * soft shadow at that edge (the strip of the left sidebar had them, while it
 * stood). At 390 px „System" was cut to „Syste" with nothing to say there
 * was more — and a filter nobody sees is no filter. Measured on scroll and
 * on resize; a shadow painted on the scroller's own background, as the
 * sidebar did, cannot work here: the segments are opaque and would cover it.
 */
function Filters({
  items,
  filter,
  onFilter,
}: {
  items: readonly TimelineItemOut[];
  filter: TimelineFilter;
  onFilter: (filter: TimelineFilter) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  // The knob glides to the pressed filter (design 2026-10-24 §6.4, Ruling B
  // of the steel plan) as `SegmentedControl`'s does — toggles, not radios.
  useGlide("pressed", groupRef, thumbRef, FILTERS.length, filter);
  const [ends, setEnds] = useState({ start: false, end: false, cover: 0 });
  function measureEnds() {
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    const next = measureRow(scroller);
    setEnds((shown) => (shown.start === next.start && shown.end === next.end && shown.cover === next.cover ? shown : next));
  }
  // The active filter stays in view with its name (UX gate round 2, #10): at
  // 390 px the row was left scrolled where Tab had taken it, and „Alles 11"
  // read „11". On arrival, on a change of filter, when the row's width
  // changes, and once the focus has left the row.
  useLayoutEffect(() => {
    revealActive(scrollerRef.current);
  }, [filter]);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller === null || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      revealActive(scroller);
      const next = measureRow(scroller);
      setEnds((shown) => (shown.start === next.start && shown.end === next.end && shown.cover === next.cover ? shown : next));
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);
  function leave(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    revealActive(scrollerRef.current);
  }
  return (
    <div
      className={styles.filterFrame}
      data-more-start={ends.start ? "" : undefined}
      data-more-end={ends.end ? "" : undefined}
      style={ends.cover > 0 ? ({ "--ende-deckt": `${ends.cover}px` } as CSSProperties) : undefined}
    >
      <div ref={scrollerRef} className={styles.filterScroll} onScroll={measureEnds}>
        <div
          ref={groupRef}
          className={`${segmented.group} ${styles.filters}`}
          role="group"
          aria-label="Filter"
          data-thumb=""
          onFocus={(event) => reveal(scrollerRef.current, event.target)}
          onBlur={leave}
        >
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={segmented.segment}
              aria-pressed={filter === option.value}
              onClick={() => onFilter(option.value)}
            >
              {option.label}{" "}
              <span className={segmented.count}>{filterTimeline(items, option.value).length}</span>
            </button>
          ))}
          {/* After the segments, so the first is still the group's first child. */}
          <span ref={thumbRef} className={segmented.thumb} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

/** Glide, unless the person asked for less motion — then jump (screen check S-2). */
function scrollBehavior(): ScrollBehavior {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth";
}

/**
 * „Zum Eintrag" (screen check S-4): the entry takes the focus — and, since a
 * focus moved by script after a mouse click is not `:focus-visible`, it is
 * marked `data-target`, which draws the ring for as long as it holds the
 * focus. It is scrolled to the upper part of the window (its
 * `scroll-margin-block-start`, 20 vh), not merely into it: at the bottom
 * edge, where `focus()` alone left it, the eye never found it.
 */
function markTarget(entryId: string) {
  const element = document.getElementById(`entry-${entryId}`);
  if (element === null) return;
  element.setAttribute("data-target", "");
  element.addEventListener("blur", () => element.removeAttribute("data-target"), { once: true });
  element.focus({ preventScroll: true });
  element.scrollIntoView?.({ block: "start", behavior: scrollBehavior() });
}

interface RecordPanelProps {
  caseId: string;
  record: CaseRecordOut;
  /** Cancelled: the record can be read, nothing more is filed into it (spec 2026-10-15 §5). */
  locked: boolean;
  intake: FileIntake;
  writing: boolean;
  draft: string;
  onDraft: (text: string) => void;
  onAnnounce: (sentence: string) => void;
  onFiled: (entryIds: string[]) => void;
  /** „Zum Eintrag" — glow, ring and scroll; the filter is reset here first, so the entry is drawn. */
  onJump: (entryId: string) => void;
  onCloseNote: () => void;
  /** The first draw's rising rows, `null` once over (`risingRows`). */
  rising: ReadonlyMap<string, number> | null;
  /** A filter was chosen: the record is drawn again, and nothing rises any more. */
  onRiseOver: () => void;
}

/**
 * The record tab: what the intake has to say, the note, filters and entries
 * — the entries in a feed of their own (design 2026-10-24 §6.1), bundled
 * (§6.3). The intake's tools stand in the panel's head beside the tabs
 * (Task 9, `IntakeTools`).
 */
function RecordPanel({
  caseId,
  record,
  locked,
  intake,
  writing,
  draft,
  onDraft,
  onAnnounce,
  onFiled,
  onJump,
  onCloseNote,
  rising,
  onRiseOver,
}: RecordPanelProps) {
  const [filter, setShownFilter] = useState<TimelineFilter>("all");
  function setFilter(next: TimelineFilter) {
    setShownFilter(next);
    onRiseOver();
  }
  const shown = filterTimeline(record.timeline, filter);
  const label = FILTERS.find((option) => option.value === filter)?.label ?? "";
  const empty = record.timeline.length === 0;

  return (
    <>
      {locked ? null : (
        <IntakeStatus
          intake={intake}
          empty={empty}
          onJumpTo={(entryId) => {
            setFilter("all");
            onJump(entryId);
          }}
        />
      )}
      {writing && !locked ? (
        <NoteForm
          caseId={caseId}
          draft={draft}
          onDraft={onDraft}
          onAnnounce={onAnnounce}
          onFiled={(entryId) => onFiled([entryId])}
          onClose={onCloseNote}
        />
      ) : null}
      {empty ? (
        intake.queue !== null ? (
          <FilingRows queue={intake.queue} />
        ) : null
      ) : (
        <>
          <Filters items={record.timeline} filter={filter} onFilter={setFilter} />
          {/* Where the new entries will stand, while they are being filed (UX gate round 2, #5). */}
          {intake.queue !== null ? <FilingRows queue={intake.queue} /> : null}
          {shown.length === 0 ? (
            <div className={styles.emptyFilter}>
              <p className={styles.quiet}>Keine Einträge unter „{label}“.</p>
              <Button variant="quiet" onClick={() => setFilter("all")}>
                Alles zeigen
              </Button>
            </div>
          ) : (
            // Settled again when the entries shown, the filter or the note
            // above them change — not on every key typed into that note.
            <Feed listed={`${shown.length} ${filter} ${writing ? "notiz" : ""}`}>
              <EntryList caseId={caseId} items={shown} bundle rising={rising} />
            </Feed>
          )}
        </>
      )}
    </>
  );
}

/**
 * A drawing's first page, small (UX gate round 3, #10 and „über 93 %"): a
 * tab called „Zeichnungen" that showed no drawing was an admin panel. The
 * page route renders it — the picture as stored, a PDF's first page as a
 * picture — in a fixed box, so rows do not jump while pictures arrive
 * (`loading="lazy"`). A page that cannot be drawn leaves the box with the
 * file symbol instead of a broken image. Decoration, `alt=""` (final review
 * of Plan D): the file's name stands beside it, visible, and a screen
 * reader would say it twice.
 */
function DocThumb({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={styles.docThumb}>
      {src !== null && !failed ? (
        // A page, and so a sheet: paper in the dark too (design 2026-10-24 §5.3).
        <img src={src} alt="" data-blatt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <FileIcon size={20} />
      )}
    </span>
  );
}

/**
 * Where a drawing came from, when the record says so (round-4 screen check,
 * R4-13) — no wire change: an attachment of a mail on file with the same
 * name, type and size, marked as already a document of the Vorgang (the
 * server answers `adoptable: false` for exactly that, or for a type the
 * reading does not take — which no drawing has). A drawing dropped in
 * directly has no such entry, and says nothing.
 *
 * The same attachment is the drawing's original, byte for byte — adopting
 * copies it (UX gate round 4, #3): a PDF, whose page route draws a picture
 * of one page and not the file, is saved from there.
 */
function originOf(
  caseId: string,
  document: DocumentOut,
  timeline: readonly TimelineItemOut[],
): { sentence: string; href: string } | null {
  for (const item of timeline) {
    if (item.kind !== "correspondence") continue;
    const entry = item.correspondence;
    const attachment = entry.attachments.find(
      (each) =>
        !each.adoptable &&
        each.filename === document.originalFilename &&
        each.mimeType === document.mimeType &&
        each.byteSize === document.byteSize,
    );
    if (attachment !== undefined) {
      return {
        sentence: `aus Mail vom ${formatDay(entry.occurredAt)}`,
        href: correspondenceFileUrl(caseId, entry.id, attachment.id),
      };
    }
  }
  return null;
}

/**
 * Where a drawing is saved from (UX gate round 4, #3): a picture's one page
 * *is* the file — the page route hands the stored image back as it is
 * (upright where EXIF said so), under its own name. A PDF is saved from the
 * mail it was adopted from, where the record knows it; its page route draws
 * a picture of one page, and no route serves a stored PDF as it came — a
 * PDF filed directly offers its page to look at and no download that would
 * be a different file under the same name.
 */
function downloadOf(
  caseId: string,
  document: DocumentOut,
  origin: { href: string } | null,
): { href: string; filename?: string } | null {
  if (origin !== null) return { href: origin.href };
  if (isPicture(document.mimeType)) {
    return { href: casePageUrl(caseId, document.id, 1), filename: document.originalFilename };
  }
  return null;
}

/**
 * The drawings of the reading pipeline (`project_document`) — each with
 * its first page as a picture (`DocThumb`); the way into a drawing is that
 * page. The per-Vorgang
 * reading (`/vorgang/:id/einlesen`, paket-a) is reached from the head's next
 * step while there is no Stand — not from here, where a Vorgang without a
 * reading (drawn by hand, an adopted attachment nobody read) would land on
 * „keine Auslese" (plan D-11).
 *
 * Each says where it came from when the record knows (R4-13), and — while
 * the Vorgang has no Stand and its stage is not over, as the head's own
 * „Umriss zeichnen →" — leads to the outline drawn over it
 * (`/umriss?case=…&vorlage=…`, the drawing as the reference beside the
 * board). Tracing, not reading: nothing is read from it (E-54).
 */
function DocumentsPanel({ caseId, record }: { caseId: string; record: CaseRecordOut }) {
  const detail = useCase(caseId);
  /** The drawing shown large, in the app (`SketchViewer`). */
  const [looking, setLooking] = useState<DocumentOut | null>(null);
  const traceable =
    record.latestVersionNo === 0 && !HANDOVER_CLOSED.has(record.case.status) && record.case.status !== "sent";
  if (detail.isPending) return <p className={styles.quiet}>Zeichnungen werden geladen …</p>;
  if (detail.error || !detail.data) return <p className={styles.quiet}>Zeichnungen nicht erreichbar.</p>;
  const documents = detail.data.documents;
  if (documents.length === 0) {
    return (
      <p className={styles.quiet}>
        Noch keine Zeichnung. „Als Zeichnung übernehmen“ an einer Mail-Anlage legt eine hierher.
      </p>
    );
  }
  return (
    // A fragment around the list, kept at its indentation: the viewer is the
    // one thing added (Task 18), and the rows below stay as they were.
    <>
    <ul className={styles.docs}>
      {documents.map((document) => {
        const view = firstPage(document.mimeType, document.originalFilename, document.pageCount);
        const origin = originOf(caseId, document, record.timeline);
        const save = downloadOf(caseId, document, origin);
        // A PDF no mail carries has no download (`downloadOf`) — said at the
        // drawing, not left as a gap beside a picture that has one
        // (round-5 screen check, R5-5).
        const noSave = save === null && view !== null ? `doc-${document.id}-nosave` : null;
        return (
          <li key={document.id} className={styles.doc}>
            <DocThumb src={view !== null ? casePageUrl(caseId, document.id, 1) : null} />
            <span className={styles.docText}>
              <span className={styles.docName}>{document.originalFilename}</span>
              <span className={`tabular ${styles.entryMeta}`}>
                {formatBytes(document.byteSize)}
                {document.pageCount ? ` · ${document.pageCount} S.` : ""}
              </span>
              {origin !== null ? <span className={styles.entryMeta}>{origin.sentence}</span> : null}
              <span className={styles.docActions}>
                {view !== null ? (
                  // In the app, large and page by page (Task 18) — it used to
                  // open the raw page in the tab and leave the Werkbank.
                  <button
                    type="button"
                    className="inline-link"
                    aria-label={view.name}
                    aria-describedby={noSave ?? undefined}
                    onClick={() => setLooking(document)}
                  >
                    {view.text}
                  </button>
                ) : null}
                {save !== null ? (
                  <DownloadLink
                    href={save.href}
                    name={document.originalFilename}
                    filename={save.filename}
                  />
                ) : noSave !== null ? (
                  // The download's place, kept (R5-5): „Ansehen" and „Umriss
                  // danach zeichnen →" stand on the same edges in every row.
                  <span className={`${styles.download} ${styles.docSlot}`} aria-hidden="true">
                    <DownloadIcon size={12} />
                    <span>Herunterladen</span>
                  </span>
                ) : null}
                {traceable && view !== null ? (
                  <Link
                    className="inline-link"
                    to={`${outlineFor(caseId)}&vorlage=${encodeURIComponent(document.id)}`}
                    aria-label={`${document.originalFilename} — Umriss danach zeichnen →`}
                  >
                    Umriss danach zeichnen →
                  </Link>
                ) : null}
              </span>
              {noSave !== null ? (
                <span id={noSave} className={`${styles.entryMeta} ${styles.docNote}`}>
                  Kein Download: eine PDF ohne Mail lässt sich hier nur ansehen.
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
    {looking !== null ? <SketchViewer caseId={caseId} document={looking} onClose={() => setLooking(null)} /> : null}
    </>
  );
}

/**
 * The way to see a drawing (screen check S-3): its first page, for every
 * type the store takes (`DOCUMENT_MIME`: PDF, JPEG, PNG) — **not** gated on
 * `pageCount`. Nothing writes that column: an adopted attachment and an
 * uploaded drawing both carry `null`, and the tab was a list of names with
 * no way into any of them. The page route renders page 1 from the store
 * whatever the column says (measured: 200 for a PNG and a PDF with
 * `pageCount: null`). Both say „Ansehen" (round-5 screen check, R5-5: one
 * offer for every drawing); since the viewer turns every page (Task 18) a
 * PDF's name says how many where that is known. The file's name goes into
 * the name, so a list of them read on its own still says which is which.
 */
function firstPage(
  mimeType: string,
  filename: string,
  pageCount: number | null,
): { text: string; name: string } | null {
  // The viewer turns every page (Task 18): the name says how many where that
  // is known, never „Seite 1“ alone (review T18-4).
  if (mimeType === "application/pdf") {
    return {
      text: "Ansehen",
      name: pageCount !== null && pageCount > 1 ? `${filename} — ${pageCount} Seiten ansehen` : `${filename} ansehen`,
    };
  }
  if (mimeType.startsWith("image/")) return { text: "Ansehen", name: `${filename} ansehen` };
  return null;
}

/** A picture, whose one page is the stored file (UX gate round 1, #8). */
function isPicture(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}

function VersionsPanel({ caseId, record }: { caseId: string; record: CaseRecordOut }) {
  const navigate = useNavigate();
  if (record.latestVersionNo === 0) {
    return (
      <p className={styles.quiet}>
        Noch kein Stand. Ein Stand entsteht, sobald ein Umriss gezeichnet oder eine Auslese übernommen ist.
      </p>
    );
  }
  const saved = record.timeline.filter(
    (item) => item.kind === "event" && item.event.eventType === "plan_version_created",
  );
  return (
    <>
      <div className={styles.versionHead}>
        <p className={styles.quiet}>Stand {record.latestVersionNo} ist der letzte.</p>
        <Button variant="quiet" onClick={() => navigate(`/vorgang/${caseId}/planung`)}>
          Zur Planung →
        </Button>
      </div>
      <EntryList caseId={caseId} items={saved} />
    </>
  );
}

type Tab = "record" | "documents" | "versions";
const ORDER: readonly Tab[] = ["record", "documents", "versions"];

/**
 * The right column of the record (D-7): a panel with three tabs flush at its
 * top, the record first (UX ruling 6). The tabs are the shared `Tabs` block:
 * one tab stop, arrows move and select, Home and End go to the ends, and a
 * marker glides under the chosen one. All three panels are in the page; only
 * the active one holds content.
 *
 * The column is the drop target (UX ruling 7), so the intake, the note
 * draft, what was just filed and the one live region live here — a drop on
 * „Zeichnungen" still files into the record and switches to it.
 */
export function Timeline({ caseId, record }: { caseId: string; record: CaseRecordOut }) {
  const [tab, setTab] = useState<Tab>("record");
  const [announcement, setAnnouncement] = useState("");
  const [writing, setWriting] = useState(false);
  const [draft, setDraft] = useState("");
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  // The one entry just filed, until the record read again draws it (S-2).
  const reveal = useRef<string | null>(null);
  const noteOpener = useRef<HTMLButtonElement>(null);
  // The record's first draw rises in, once (design 2026-10-24 §6.4): the rows
  // it drew first, so an entry that arrives later or a row drawn again does
  // not. Here, not in the record tab — a tab and back is no first draw.
  const [rising, setRising] = useState<ReadonlyMap<string, number> | null>(() => risingRows(record.timeline));
  useEffect(() => {
    const timer = window.setTimeout(() => setRising(null), RISE_MS);
    return () => window.clearTimeout(timer);
  }, []);

  /**
   * Screen check S-2: what was just filed glows — and one new entry (a note,
   * a single file) is brought into view once it is in the page, as little as
   * needed (`nearest`): dated yesterday it sorts in under the fold, and the
   * glow went on where nobody saw it. A batch stays where it is; its result
   * block offers „Zum Eintrag" at every filed line instead.
   */
  function filed(ids: string[]) {
    if (ids.length === 0) return;
    setFresh(new Set(ids));
    reveal.current = ids.length === 1 ? (ids[0] ?? null) : null;
  }

  function jump(entryId: string) {
    reveal.current = null;
    setFresh(new Set([entryId]));
    // After the render that resets the filter, so the entry is in the page.
    window.setTimeout(() => markTarget(entryId), 0);
  }

  const intake = useFileIntake(caseId, setAnnouncement, filed);
  const labels: Record<Tab, string> = {
    // „Verlauf", not „Akte" (Task 9, UX-Gutachten 1, B-1): the screen is the
    // Akte — its tab stood under the word a second time.
    record: `Verlauf · ${record.timeline.length}`,
    // „Zeichnungen", not „Unterlagen" (UX gate round 2, #8): the stage beside
    // it says „Unterlagen da" for the papers a person ticked off, and the
    // tab held only the drawings the reading takes — „Unterlagen da" stood
    // next to „Unterlagen · 0". „Unterlagen" stays the stage's word.
    documents: `Zeichnungen · ${record.documentsCount}`,
    versions: `Stände · ${record.latestVersionNo}`,
  };
  const client = useQueryClient();
  // A cancelled Vorgang takes no write: no intake, no note, no „zurückziehen",
  // no „als Zeichnung übernehmen" — the server refuses all of them with a 409
  // (`case_cancelled`), and the record does not offer what it would refuse.
  const locked = record.case.status === "cancelled";
  const [retracting, setRetracting] = useState<CorrespondenceOut | null>(null);
  // The mail whose customer is being taken (spec 2026-10-20 §3.3).
  const [customerOf, setCustomerOf] = useState<CorrespondenceOut | null>(null);
  // The entry whose drawing is being chosen (UX-Gutachten 2, B-1).
  const [choosing, setChoosing] = useState<CorrespondenceOut | null>(null);
  // The entry just retracted — focused once its dialog is truly gone (T9).
  const retracted = useRef<string | null>(null);
  const [adopted, setAdopted] = useState<ReadonlyMap<string, { sentence: string; done: boolean }>>(new Map());

  function markAdopted(attachment: AttachmentOut, sentence: string, done: boolean) {
    setAnnouncement(sentence);
    setAdopted((previous) => new Map(previous).set(attachment.id, { sentence, done }));
  }

  /** „Als Zeichnung übernehmen" (`adopt.ts`): the answer is said at the entry and aloud. */
  async function adopt(entry: CorrespondenceOut, attachment: AttachmentOut) {
    const { sentence, done } = await adoptIntoDrawings(client, caseId, entry, attachment);
    markAdopted(attachment, sentence, done);
  }

  // What was just filed is marked for 2 s — a sunken ground and an ink edge
  // (UX gate round 2, #5) — then settles (UX ruling 7) —
  // counted from when it is on the page, not from when the answer came
  // (S-2): a slow re-read of the record used the whole glow up before the
  // entry was there to show it. From a timer, so no state is set
  // synchronously in the effect.
  const freshShown = record.timeline.some(
    (item) => item.kind === "correspondence" && fresh.has(item.correspondence.id),
  );
  useEffect(() => {
    if (fresh.size === 0 || !freshShown) return undefined;
    const timer = window.setTimeout(() => setFresh(new Set()), FRESH_MS);
    return () => window.clearTimeout(timer);
  }, [fresh, freshShown]);

  // The one entry just filed, once the record read again has drawn it.
  useEffect(() => {
    const id = reveal.current;
    if (id === null) return;
    const element = document.getElementById(`entry-${id}`);
    if (element === null) return;
    reveal.current = null;
    element.scrollIntoView?.({ block: "nearest", behavior: scrollBehavior() });
  }, [record, fresh, tab]);

  function content(id: Tab): ReactNode {
    if (id === "record") {
      return (
        <RecordPanel
          caseId={caseId}
          record={record}
          locked={locked}
          intake={intake}
          writing={writing}
          draft={draft}
          onDraft={setDraft}
          onAnnounce={setAnnouncement}
          onFiled={filed}
          onJump={jump}
          onCloseNote={() => {
            setWriting(false);
            // After the form is gone, focus goes back to where writing started.
            window.setTimeout(() => noteOpener.current?.focus(), 0);
          }}
          rising={rising}
          onRiseOver={() => setRising(null)}
        />
      );
    }
    if (id === "documents") return <DocumentsPanel caseId={caseId} record={record} />;
    return <VersionsPanel caseId={caseId} record={record} />;
  }

  return (
    <EntryMarksContext.Provider
      value={{
        fresh,
        adopted,
        onRetract: locked ? undefined : (entry) => setRetracting(entry),
        onAdopt: locked ? undefined : (entry, attachment) => void adopt(entry, attachment),
        onChooseDrawing: locked ? undefined : (entry) => setChoosing(entry),
        onCustomer: locked ? undefined : (entry) => setCustomerOf(entry),
      }}
    >
      <DropTarget
        title={record.case.title}
        locked={locked}
        busy={intake.busy}
        onFiles={(files) => {
          setTab("record");
          void intake.place(files);
        }}
        onRefused={(files) => {
          setTab("record");
          intake.refuseWhileBusy(files);
        }}
      >
        {/* The column is „Akte"; its tab list chooses a view of it — one
            name each, not „Akte" twice (record fix wave, T7). */}
        <section className={`${styles.panel} ${styles.record}`} aria-label="Akte">
          {/* One region for everything asynchronous in the column, present
              before its text. Hidden: what it says also stands on screen —
              the result block, the new entry glowing, the entry in focus. */}
          <p role="status" className="visually-hidden">
            {announcement}
          </p>
          <Tabs
            label="Ansicht"
            items={ORDER.map((id) => ({ key: id, label: labels[id], panel: content(id) }))}
            selected={tab}
            onSelect={setTab}
            idPrefix="record"
            className={styles.tabsBleed}
            panelClassName={styles.panelBody}
            // The intake's tools in the panel's head (Task 9, B-1) — with the
            // record's own tab, where they file into; a drop on any tab still
            // lands there (`DropTarget`).
            end={
              tab === "record" && !locked ? (
                <IntakeTools intake={intake} onWriteNote={() => setWriting(true)} noteOpenerRef={noteOpener} />
              ) : null
            }
          />
        </section>
        {retracting !== null ? (
          <RetractDialog
            caseId={caseId}
            entry={retracting}
            onClose={() => setRetracting(null)}
            onDone={(sentence) => {
              setAnnouncement(sentence);
              retracted.current = retracting.id;
              setRetracting(null);
            }}
            // After the dialog has handed the focus back to the menu button —
            // `onClosed` runs after that, in the same cleanup — move it on to
            // the entry that changed (record fix wave, T9: two nested
            // timeouts lost that race live).
            onClosed={() => {
              const id = retracted.current;
              retracted.current = null;
              if (id !== null) document.getElementById(`entry-${id}`)?.focus();
            }}
          />
        ) : null}
        {choosing !== null ? (
          <AdoptDialog
            entry={choosing}
            // Closed first — the focus goes back to the menu's button — then
            // adopted the way the menu's own item does: the answer at the
            // entry and aloud.
            onChoose={(attachment) => {
              setChoosing(null);
              void adopt(choosing, attachment);
            }}
            onClose={() => setChoosing(null)}
          />
        ) : null}
        {customerOf !== null ? (
          <CustomerFromMailDialog
            caseId={caseId}
            entry={customerOf}
            onClose={() => setCustomerOf(null)}
            onDone={(customer) => {
              // The dialog's own region goes with it: the column's says it.
              setAnnouncement(customerAssignedSentence(customer));
              setCustomerOf(null);
            }}
          />
        ) : null}
      </DropTarget>
    </EntryMarksContext.Provider>
  );
}
